import { useEffect, useState } from 'react';

import { cx } from '@emotion/css';
import { Col, Form } from 'antd';
import { Formik } from 'formik';
import { isEmpty, isUndefined } from 'lodash';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { doClearGetQuote, doClearGetQuoteChart } from 'src/actions';
import { FilterBarConstant, NavigatePathConstant, OrderConstant } from 'src/constants';
import { OfferOrderStatusDto, OrderActionDto, OrderDurationDto, OrderTypeDto } from 'src/dtos';
import { useAccountBalance, useOfferOrders, useOrders, useSubmittedAccountSelector } from 'src/hooks';
import {
  MModalBody,
  MModalFooter,
  MButton,
  MFormInput,
  MFormSelect,
  MFormTextField,
  MTypography,
  MTooltip,
  MModalTitle,
  MFormCurrencyInput,
  CurrencyField,
  MAlert,
} from 'src/lib';
import { OrderDurationLabel, Quote, OrderTypeLabel, OrderStatusLabel, OfferOrderStatusLabel } from 'src/models';
import { BaseStyle, AlignItems, JustifyContent, Display, ColorConstant, Spacing } from 'src/styles';
import { findEstimatedTotal } from 'src/utils';
import * as Yup from 'yup';

const validationSchema = Yup.object({
  type: Yup.string()
    .oneOf(
      [
        OrderTypeDto.Market,
        OrderTypeDto.Limit,
        OrderTypeDto.StopMarket,
        OrderTypeDto.StopLimit,
        OrderTypeDto.MarketOnClose,
      ],
      `Order Type must be one of: ${Object.values(OrderTypeLabel).join(', ')}`,
    )
    .required(),
  duration: Yup.string().oneOf([OrderDurationDto.Day, OrderDurationDto.GTC]).required(),
  quantity: Yup.number().integer().positive().min(1, 'Quantity must be at least 1').required('Quantity is required'),
  price: Yup.number()
    .positive()
    .when('type', {
      is: (val: string) => val === OrderTypeDto.Limit || val === OrderTypeDto.StopLimit,
      then: Yup.number()
        .min(0.01, 'Limit Price must be at least 0.01')
        .required('Limit Price is required for Limit and Stop Limit orders'),
      otherwise: Yup.number().notRequired(),
    }),
  stop: Yup.number()
    .positive()
    .when('type', {
      is: (val: string) => val === OrderTypeDto.StopMarket || val === OrderTypeDto.StopLimit,
      then: Yup.number()
        .min(0.01, 'Stop Price must be at least 0.01')
        .required('Stop Price is required for Stop and Stop Limit orders'),
      otherwise: Yup.number().notRequired(),
    }),
});

export interface SecurityOrderFormValues {
  type?: OrderTypeDto;
  duration?: OrderDurationDto;
  quantity?: number;
  price?: number;
  stop?: number;
  estimatedTotal?: number;
}

const orderTypeOptionList = [
  { label: OrderTypeLabel.Market, value: OrderTypeDto.Market },
  { label: OrderTypeLabel.Limit, value: OrderTypeDto.Limit },
  { label: OrderTypeLabel.StopMarket, value: OrderTypeDto.StopMarket },
  { label: OrderTypeLabel.StopLimit, value: OrderTypeDto.StopLimit },
  { label: OrderTypeLabel.MarketOnClose, value: OrderTypeDto.MarketOnClose },
];

const orderExpirationOptionList = [
  { label: OrderDurationLabel.Day, value: OrderDurationDto.Day },
  { label: OrderDurationLabel.GTC, value: OrderDurationDto.GTC },
];

interface SecurityHoldingTradeOrderStepProps {
  action?: OrderActionDto;
  initialValues?: SecurityOrderFormValues;
  quote: Quote;
  quantity?: number;
  onClose: () => void;
  onBack?: () => void;
  onReview?: (values: SecurityOrderFormValues) => void;
  onLastPrice: () => void;
  onServiceFee: () => void;
}

export const SecurityHoldingTradeOrderStep = ({
  action,
  initialValues = { type: OrderTypeDto.Market, duration: OrderDurationDto.Day },
  quote,
  quantity,
  onBack,
  onReview,
  onClose,
  onLastPrice,
  onServiceFee,
}: SecurityHoldingTradeOrderStepProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cashAvailable } = useAccountBalance({ skip: true });
  const [hasEnoughCash, setHasEnoughCash] = useState(true);
  const { account } = useSubmittedAccountSelector();

  // NOTE: with the introduction of orders to summary page then the offer order, and order list must be
  // fetched from the summary page, and skipped from the order step.
  const { offerOrderList, isOfferOrderListLoading } = useOfferOrders();
  const { orderList, isOrderListLoading } = useOrders();

  const uncancelledOfferOrders = offerOrderList.filter(anOfferOrder =>
    [
      OfferOrderStatusDto.PendingFunds,
      OfferOrderStatusDto.PendingAction,
      OfferOrderStatusDto.PendingOfferClose,
    ].includes(anOfferOrder.status.value),
  );

  const uncancelledBuyOrders = orderList.filter(
    anOrder => OrderConstant.UNCANCELLED_STATUS_LIST.includes(anOrder.status.value) && anOrder.action.isBuy,
  );

  const cleanQuoteInformation = () => {
    dispatch(doClearGetQuote());
    dispatch(doClearGetQuoteChart());
  };

  return (
    <Formik<SecurityOrderFormValues>
      initialValues={initialValues}
      initialErrors={{}}
      validationSchema={validationSchema}
      validateOnChange
      enableReinitialize
      onSubmit={values => {
        onReview?.(values);
      }}>
      {form => {
        useEffect(() => {
          const estimatedTotal = findEstimatedTotal({
            action,
            type: form.values.type,
            quantity: form.values.quantity,
            price: form.values.price,
            stop: form.values.stop,
            value: quote.last,
          });
          form.setFieldValue('estimatedTotal', estimatedTotal, false);

          if (action === OrderActionDto.Buy) {
            setHasEnoughCash(estimatedTotal <= cashAvailable);
          }
        }, [form.values.type, form.values.price, form.values.stop, form.values.quantity, action]);

        return (
          <Form autoComplete='off' style={{ width: '100%' }}>
            <MModalTitle>Security {action} Order</MModalTitle>
            <MModalBody isLoading={isOfferOrderListLoading || isOrderListLoading}>
              <Col span={24} className={Spacing.my12}>
                <MTypography variant='h2' align='center' color={ColorConstant.BRAND6} weight='semibold'>
                  {action} {quote.symbol}
                </MTypography>
              </Col>
              <Col span={24} className={Spacing.my24}>
                <MTypography variant='h6' align='center' weight='light'>
                  Cash Available: <CurrencyField value={cashAvailable} />
                </MTypography>
              </Col>
              <Col span={24}>
                <MFormSelect
                  label='Order Type'
                  placeholder='Select'
                  options={orderTypeOptionList}
                  defaultValue={initialValues.type}
                  value={form.values.type}
                  onChange={value => {
                    form.setFieldValue('type', value);

                    if (value === OrderTypeDto.Market || value === OrderTypeDto.MarketOnClose) {
                      form.setFieldValue('price', undefined);
                      form.setFieldValue('stop', undefined);
                    }

                    if (value === OrderTypeDto.Limit) {
                      form.setFieldValue('stop', undefined);
                    }

                    if (value === OrderTypeDto.StopMarket) {
                      form.setFieldValue('price', undefined);
                    }
                  }}
                  error={form.errors.type}
                  name='type'
                  className={cx(Spacing.my0, Spacing.py16)}
                  testId='security-order-type-select'
                />
              </Col>
              <Col span={24}>
                <MFormTextField
                  label={
                    <>
                      Last Price
                      <MTooltip showIcon onClick={() => onLastPrice()} />
                    </>
                  }
                  value={<CurrencyField value={quote.last} />}
                />
              </Col>
              {!isUndefined(quantity) && (
                <Col span={24}>
                  <MFormTextField label='Shares Held' value={quantity} />
                </Col>
              )}
              {form.values.type === OrderTypeDto.Limit || form.values.type === OrderTypeDto.StopLimit ? (
                <Col span={24}>
                  <MFormCurrencyInput
                    label='Limit Price'
                    defaultValue={initialValues.price?.toString()}
                    value={form.values.price}
                    onChange={value => {
                      form.setFieldValue('price', value);
                    }}
                    error={form.errors.price}
                    name='price'
                    autoComplete='off'
                    className={cx(Spacing.my0, Spacing.py16)}
                    testId='security-order-price'
                  />
                </Col>
              ) : null}
              {form.values.type === OrderTypeDto.StopMarket || form.values.type === OrderTypeDto.StopLimit ? (
                <Col span={24}>
                  <MFormCurrencyInput
                    label='Stop Price'
                    defaultValue={initialValues.stop?.toString()}
                    value={form.values.stop}
                    onChange={value => {
                      form.setFieldValue('stop', value);
                    }}
                    error={form.errors.stop}
                    name='stop'
                    autoComplete='off'
                    className={cx(Spacing.my0, Spacing.py16)}
                    testId='security-order-stop'
                  />
                </Col>
              ) : null}
              <Col span={24}>
                <MFormInput
                  label={`Shares to ${action}`}
                  defaultValue={initialValues.quantity?.toString()}
                  value={form.values.quantity}
                  onChange={value => {
                    form.setFieldValue('quantity', value);
                  }}
                  error={form.errors.quantity}
                  name='quantity'
                  autoComplete='off'
                  className={cx(Spacing.my0, Spacing.py16)}
                  testId='security-order-quantity'
                />
              </Col>
              <Col span={24}>
                <MFormSelect
                  label='Order Expiration'
                  placeholder='Select'
                  options={orderExpirationOptionList}
                  defaultValue={initialValues.duration}
                  value={form.values.duration}
                  onChange={value => {
                    form.setFieldValue('duration', value);
                  }}
                  error={form.touched.duration ? form.errors.duration : undefined}
                  name='duration'
                  className={cx(Spacing.my0, Spacing.py16)}
                  testId='security-order-duration'
                />
              </Col>
              <Col span={24}>
                <MFormTextField
                  label={
                    <>
                      Service Fee
                      <MTooltip showIcon onClick={() => onServiceFee()} />
                    </>
                  }
                  value='$10.00'
                />
              </Col>
              <Col span={24}>
                <MFormTextField label='Estimated Total' value={<CurrencyField value={form.values.estimatedTotal} />} />
              </Col>
              {!hasEnoughCash && uncancelledOfferOrders.length > 0 && (
                <MAlert
                  type='error'
                  description={
                    <>
                      You have open Offer Order(s). Click{' '}
                      <a
                        onClick={() => {
                          navigate(
                            `/main/history?show=${JSON.stringify([
                              FilterBarConstant.ShowFilterCategory.OfferOrder,
                            ])}&filterBy=${JSON.stringify([
                              `${OfferOrderStatusLabel.PendingFunds}_${FilterBarConstant.ByFilterCategory.OfferOrder}`,
                              `${OfferOrderStatusLabel.PendingOfferClose}_${FilterBarConstant.ByFilterCategory.OfferOrder}`,
                              `${OfferOrderStatusLabel.PendingAction}_${FilterBarConstant.ByFilterCategory.OfferOrder}`,
                            ])}`,
                          );
                          cleanQuoteInformation();
                        }}>
                        here{' '}
                      </a>{' '}
                      to view or cancel order(s) to free up funds, or click{' '}
                      <a
                        onClick={() => {
                          navigate(`/accounts/${account.id}/${NavigatePathConstant.MONEY_TRANSFERS}`);
                          cleanQuoteInformation();
                        }}>
                        here
                      </a>{' '}
                      to add funds and place this Security Buy Order.
                    </>
                  }
                />
              )}
              {!hasEnoughCash && uncancelledBuyOrders.length > 0 && (
                <MAlert
                  type='error'
                  description={
                    <>
                      You have open Security Buy Order(s). Click{' '}
                      <a
                        onClick={() => {
                          navigate(
                            `/${NavigatePathConstant.MAIN}/history?show=${JSON.stringify([
                              FilterBarConstant.ShowFilterCategory.BuyOrder,
                            ])}&filterBy=${JSON.stringify([
                              `${OrderStatusLabel.PROCESSING}_${FilterBarConstant.ByFilterCategory.Order}`,
                              `${OrderStatusLabel.PENDING}_${FilterBarConstant.ByFilterCategory.Order}`,
                            ])}`,
                          );
                          cleanQuoteInformation();
                        }}>
                        here{' '}
                      </a>{' '}
                      to view or cancel order(s) to free up funds, or click{' '}
                      <a
                        onClick={() => {
                          navigate(`/accounts/${account.id}/${NavigatePathConstant.MONEY_TRANSFERS}`);
                          cleanQuoteInformation();
                        }}>
                        here
                      </a>{' '}
                      to add funds and place this Security Buy Order.
                    </>
                  }
                />
              )}
              {!hasEnoughCash && isEmpty(uncancelledOfferOrders) && isEmpty(uncancelledBuyOrders) && (
                <MAlert
                  type='error'
                  description={
                    <>
                      You do not have enough money to successfully complete the order. Click{' '}
                      <a
                        onClick={() => {
                          navigate(`/accounts/${account.id}/${NavigatePathConstant.MONEY_TRANSFERS}`);
                          cleanQuoteInformation();
                        }}>
                        here
                      </a>{' '}
                      to add funds and place this Security Buy Order.
                    </>
                  }
                />
              )}
            </MModalBody>
            <MModalFooter>
              <Col span={24} className={cx(Display.flex, AlignItems.center, JustifyContent.spaceBetween)}>
                <MButton type='tertiary' onClick={onClose}>
                  Close
                </MButton>
                <Col className={BaseStyle.levelRight}>
                  <MButton type='secondary' onClick={onBack}>
                    Back
                  </MButton>
                  <MButton disabled={!form.isValid || !hasEnoughCash} onClick={form.submitForm} htmlType='submit'>
                    Review
                  </MButton>
                </Col>
              </Col>
            </MModalFooter>
          </Form>
        );
      }}
    </Formik>
  );
};
